// Animation utilities - lazy loaded anime.js implementation

let animeInstance = null;

export const initAnimations = async () => {
  if (!animeInstance) {
    try {
      // Lazy load anime.js with different import approaches
      const animeModule = await import('animejs');
      
      // Try different export formats for anime.js v4
      if (typeof animeModule.default === 'function') {
        animeInstance = animeModule.default;
      } else if (typeof animeModule === 'function') {
        animeInstance = animeModule;
      } else if (animeModule.anime && typeof animeModule.anime === 'function') {
        animeInstance = animeModule.anime;
      } else {
        console.error('Could not find anime function in module:', animeModule);
        throw new Error('Anime.js function not found');
      }
      
      console.log('Anime.js loaded for animations');
    } catch (error) {
      console.error('Failed to load anime.js:', error);
      throw error;
    }
  }
  
  return animeInstance;
};

// Pulse animation for urgent elements (timers < 30s)
export const pulseAnimation = async (element) => {
  const anime = await initAnimations();
  
  return anime({
    targets: element,
    scale: [1, 1.05, 1],
    duration: 1000,
    easing: 'easeInOutSine',
    loop: true
  });
};

// Fade in animation for notifications
export const fadeInAnimation = async (element) => {
  try {
    const anime = await initAnimations();
    
    return anime({
      targets: element,
      opacity: [0, 1],
      translateY: [-20, 0],
      duration: 300,
      easing: 'easeOutQuad'
    });
  } catch (error) {
    console.warn('Animation failed, using CSS fallback:', error);
    // CSS fallback
    element.style.opacity = '1';
    element.style.transform = 'translateY(0)';
    return Promise.resolve();
  }
};

// Fade out animation for notifications
export const fadeOutAnimation = async (element) => {
  try {
    const anime = await initAnimations();
    
    return anime({
      targets: element,
      opacity: [1, 0],
      translateY: [0, -20],
      duration: 300,
      easing: 'easeInQuad'
    });
  } catch (error) {
    console.warn('Animation failed, using CSS fallback:', error);
    // CSS fallback
    element.style.opacity = '0';
    element.style.transform = 'translateY(-20px)';
    return Promise.resolve();
  }
};

// Bounce animation for rewards
export const bounceAnimation = async (element) => {
  const anime = await initAnimations();
  
  return anime({
    targets: element,
    scale: [1, 1.2, 1],
    duration: 600,
    easing: 'easeOutBounce'
  });
};

// Shake animation for errors
export const shakeAnimation = async (element) => {
  const anime = await initAnimations();
  
  return anime({
    targets: element,
    translateX: [-10, 10, -10, 10, 0],
    duration: 500,
    easing: 'easeInOutSine'
  });
};

// Progress bar animation
export const progressAnimation = async (element, percentage) => {
  const anime = await initAnimations();
  
  return anime({
    targets: element,
    width: `${percentage}%`,
    duration: 800,
    easing: 'easeOutQuart'
  });
};

// Cleanup all animations
export const cleanupAnimations = () => {
  if (animeInstance) {
    // Remove all running animations
    animeInstance.remove('*');
  }
};